import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Upload, CheckCircle2, FileText, Sparkles } from "lucide-react";

export default function UploadResume() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resume, setResume] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/resume/active").then((r) => setResume(r.data)).catch(() => {});
  }, []);

  const upload = async (f) => {
    if (!f) return;
    setLoading(true); setErr("");
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/resume/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResume(r.data);
    } catch (e) {
      if (e.response?.status === 429) {
        setErr("You've hit the hourly upload limit. Please wait a bit and try again.");
      } else {
        setErr(e.response?.data?.detail || "Upload failed. Please try again.");
      }
    } finally { setLoading(false); }
  };

  const parsed = resume?.parsed || {};

  return (
    <div className="space-y-6 max-w-5xl" data-testid="upload-resume-page">
      <div className="jr-card" style={{ background: "linear-gradient(135deg, rgba(139,92,246,0.18), rgba(34,211,238,0.08))" }}>
        <label className="block border-2 border-dashed border-violet-500/30 rounded-xl p-12 text-center cursor-pointer hover:bg-white/[0.03]">
          <Upload className="w-10 h-10 text-violet-300 mx-auto mb-3" />
          <div className="text-lg text-zinc-200">{loading ? "Parsing with Claude Sonnet 4.5..." : "Drop your resume or click to browse"}</div>
          <div className="text-xs text-zinc-500 mt-1">PDF, DOCX, TXT (Max 10MB)</div>
          <input data-testid="resume-file-upload" type="file" accept=".pdf,.docx,.txt" className="hidden"
            onChange={(e) => upload(e.target.files[0])} />
          <div className="mt-4 inline-block jr-btn-primary">{loading ? "Uploading..." : "Browse Files"}</div>
        </label>
        {err && <div className="text-rose-300 text-sm mt-3">{err}</div>}
      </div>

      {resume && (
        <div className="jr-card jr-fade-in" data-testid="parsed-resume">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            <div className="font-semibold text-white">Resume Parsed Successfully</div>
            <span className="jr-pill bg-violet-500/20 text-violet-200">CV Parser Agent</span>
          </div>
          <div className="grid md:grid-cols-2 gap-6 text-sm">
            <div>
              <div className="text-zinc-400 text-xs mb-1">Name</div>
              <div className="text-white">{parsed.name || "—"}</div>
            </div>
            <div>
              <div className="text-zinc-400 text-xs mb-1">Primary Role</div>
              <div className="text-white">{parsed.primary_role || "—"}</div>
            </div>
            <div>
              <div className="text-zinc-400 text-xs mb-1">Seniority</div>
              <div className="text-white">{parsed.seniority_level || "—"}</div>
            </div>
            <div>
              <div className="text-zinc-400 text-xs mb-1">Years of Experience</div>
              <div className="text-white">{parsed.years_experience ?? "—"}</div>
            </div>
            <div className="md:col-span-2">
              <div className="text-zinc-400 text-xs mb-1">Summary</div>
              <div className="text-zinc-200">{parsed.summary || "—"}</div>
            </div>
            <div className="md:col-span-2">
              <div className="text-zinc-400 text-xs mb-2">Skills</div>
              <div className="flex flex-wrap gap-1.5">
                {(parsed.skills || []).map((s) => (
                  <span key={s} className="jr-pill bg-white/5 text-zinc-200">{s}</span>
                ))}
              </div>
            </div>
            <div className="md:col-span-2">
              <div className="text-zinc-400 text-xs mb-2">Profile Strength</div>
              <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-emerald-400 to-cyan-400"
                  style={{ width: `${parsed.profile_strength || 70}%` }} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
